import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';

const DURATION_LABELS = { WEEK: 'Woche', MONTH: 'Monat', YEAR: 'Jahr', UNLIMITED: 'Unbegrenzt', TRIAL: 'Test (30 Tage)' };

function LicenseManagement() {
  const [programs, setPrograms] = useState([]);
  const [selectedProgramId, setSelectedProgramId] = useState('');
  const [licenses, setLicenses] = useState([]);
  const [groups, setGroups] = useState([]);
  const [stats, setStats] = useState(null);

  const [newLicenseUserLimit, setNewLicenseUserLimit] = useState('');
  const [newLicenseDurationType, setNewLicenseDurationType] = useState('MONTH');
  const [newLicenseProductId, setNewLicenseProductId] = useState('');
  const [editingLicense, setEditingLicense] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [expandedLicenseId, setExpandedLicenseId] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      const r = await api.get('/licenses/stats');
      setStats(r.data);
    } catch { /* optional */ }
  }, []);

  const fetchPrograms = useCallback(async () => {
    try {
      const r = await api.get('/programs/');
      setPrograms(r.data);
    } catch { setError('Fehler beim Laden der Programme.'); }
  }, []);

  const fetchLicenses = useCallback(async () => {
    if (!selectedProgramId) { setLicenses([]); return; }
    setLoading(true); setError('');
    try {
      const r = await api.get(`/licenses/?program_id=${selectedProgramId}`);
      setLicenses(r.data);
    } catch { setError('Fehler beim Laden der Lizenzen.'); }
    finally { setLoading(false); }
  }, [selectedProgramId]);

  const fetchGroups = useCallback(async () => {
    if (!selectedProgramId) { setGroups([]); return; }
    try {
      const r = await api.get(`/products/groups?program_id=${selectedProgramId}`);
      setGroups(r.data);
    } catch { /* Produkte optional, kein hard error */ }
  }, [selectedProgramId]);

  useEffect(() => { fetchStats(); fetchPrograms(); }, [fetchStats, fetchPrograms]);
  useEffect(() => { fetchLicenses(); fetchGroups(); }, [fetchLicenses, fetchGroups]);

  const allProducts = groups.flatMap(g => g.products.map(p => ({ ...p, groupName: g.name })));

  const handleProductChange = (productId) => {
    setNewLicenseProductId(productId);
    if (productId) {
      const product = allProducts.find(p => String(p.id) === productId);
      if (product) setNewLicenseDurationType(product.duration_type);
    }
  };

  const handleAddOrUpdateLicense = async (event) => {
    event.preventDefault();
    if (!selectedProgramId) { setError('Bitte zuerst ein Programm auswählen.'); return; }
    setLoading(true); setError('');
    const licenseData = {
      program_id: parseInt(selectedProgramId),
      product_id: newLicenseProductId ? parseInt(newLicenseProductId) : null,
      user_limit: newLicenseUserLimit ? parseInt(newLicenseUserLimit) : null,
      duration_type: newLicenseDurationType,
      is_active: true,
    };
    // Laufzeit nur senden, wenn sie geändert wurde (Tests werden nur über /licenses/trials vergeben)
    if (editingLicense && newLicenseDurationType === editingLicense.duration_type) {
      delete licenseData.duration_type;
    }
    try {
      if (editingLicense) {
        await api.put(`/licenses/${editingLicense.id}`, licenseData);
        setEditingLicense(null);
      } else {
        await api.post('/licenses/', licenseData);
      }
      setNewLicenseUserLimit('');
      setNewLicenseDurationType('MONTH');
      setNewLicenseProductId('');
      fetchLicenses();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(`Fehler beim Speichern der Lizenz.${typeof detail === 'string' ? ' ' + detail : ''}`);
    }
    finally { setLoading(false); }
  };

  const handleEntitlementFromProduct = async (license) => {
    if (!window.confirm('Plan, Module und Kundenlimit neu vom Produkt übernehmen? Die Entitlement-Version wird erhöht.')) return;
    setLoading(true); setError('');
    try {
      await api.post(`/licenses/${license.id}/entitlement`, { from_product: true, reason: 'Admin-Panel' });
      fetchLicenses();
    } catch { setError('Fehler beim Übernehmen des Entitlements.'); }
    finally { setLoading(false); }
  };

  const handleDeleteLicense = async (id) => {
    if (!window.confirm('Lizenz wirklich löschen?')) return;
    setLoading(true); setError('');
    try {
      await api.delete(`/licenses/${id}`);
      fetchLicenses();
    } catch { setError('Fehler beim Löschen der Lizenz.'); }
    finally { setLoading(false); }
  };

  const handleRemoveActivation = async (licenseId) => {
    if (!window.confirm('Registrierung entfernen? Die Lizenz kann danach auf einem anderen Gerät aktiviert werden.')) return;
    setLoading(true); setError('');
    try {
      await api.delete(`/licenses/${licenseId}/activation`);
      fetchLicenses();
    } catch { setError('Fehler beim Entfernen der Registrierung.'); }
    finally { setLoading(false); }
  };

  const handleToggleActive = async (license) => {
    setLoading(true); setError('');
    try {
      await api.put(`/licenses/${license.id}`, { is_active: !license.is_active });
      fetchLicenses();
    } catch { setError('Fehler beim Aktualisieren der Lizenz.'); }
    finally { setLoading(false); }
  };

  const startEditing = (license) => {
    setEditingLicense(license);
    setNewLicenseUserLimit(license.user_limit || '');
    setNewLicenseDurationType(license.duration_type);
    setNewLicenseProductId(license.product_id ? String(license.product_id) : '');
  };

  const cancelEditing = () => {
    setEditingLicense(null);
    setNewLicenseUserLimit('');
    setNewLicenseDurationType('MONTH');
    setNewLicenseProductId('');
  };

  const toggleDetails = (licenseId) => {
    setExpandedLicenseId(prev => (prev === licenseId ? null : licenseId));
  };

  const formatDate = (ds) => {
    if (!ds) return '–';
    return new Date(ds).toLocaleString('de-DE', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const selectedProgram = programs.find(p => p.id === parseInt(selectedProgramId));

  return (
    <div>
      <div className="page-heading">
        <h2>Lizenzen</h2>
        <div className="program-selector">
          <label htmlFor="program-select">Programm</label>
          <select
            id="program-select"
            className="form-select"
            style={{ width: 'auto', minWidth: '200px' }}
            value={selectedProgramId}
            onChange={(e) => setSelectedProgramId(e.target.value)}
          >
            <option value="">– Programm auswählen –</option>
            {programs.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* ── Statistik ── */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', marginBottom: '24px' }}>
          <StatCard label="Lizenzen gesamt" value={stats.total} color="var(--blue)" />
          <StatCard label="Aktiv" value={stats.active} color="var(--green)" />
          <StatCard label="Inaktiv" value={stats.inactive} color="var(--text-dim)" />
          <StatCard
            label="Letzte Aktivierung"
            value={stats.last_activation ? formatDate(stats.last_activation) : '–'}
            color="var(--cyan)"
            small
          />
          <StatCard
            label="Zuletzt erstellt"
            value={stats.last_created ? formatDate(stats.last_created) : '–'}
            color="var(--violet)"
            small
          />
        </div>
      )}

      {/* ── Pro-Programm ── */}
      {stats && stats.by_program && stats.by_program.length > 0 && (
        <div className="card" style={{ marginBottom: '24px', padding: '16px 20px' }}>
          <p className="card-title" style={{ marginBottom: '12px' }}>Lizenzen pro Programm</p>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {stats.by_program.map(p => (
              <div key={p.name} style={{
                background: 'var(--surface-2)', border: '1px solid var(--border)',
                borderRadius: '10px', padding: '10px 16px', minWidth: '140px',
              }}>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>{p.name}</div>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'baseline' }}>
                  <span style={{ fontSize: '20px', fontWeight: 800, color: 'var(--text)' }}>{p.total}</span>
                  <span style={{ fontSize: '11px', color: 'var(--green)' }}>{p.active} aktiv</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {selectedProgramId && (
        <>
          <div className="card" style={{ marginBottom: '24px' }}>
            <p className="card-title">
              {editingLicense ? 'Lizenz bearbeiten' : `Neue Lizenz – ${selectedProgram?.name}`}
            </p>
            <form onSubmit={handleAddOrUpdateLicense}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                {allProducts.length > 0 && (
                  <div className="form-group" style={{ margin: 0, gridColumn: '1 / -1' }}>
                    <label className="form-label">Produkt (optional)</label>
                    <select
                      className="form-select"
                      value={newLicenseProductId}
                      onChange={e => handleProductChange(e.target.value)}
                      disabled={loading}
                    >
                      <option value="">– Kein Produkt –</option>
                      {groups.map(g => (
                        <optgroup key={g.id} label={g.name}>
                          {g.products.map(p => (
                            <option key={p.id} value={p.id}>
                              {p.name} ({parseFloat(p.price).toFixed(2)} {p.currency} / {DURATION_LABELS[p.duration_type] || p.duration_type})
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>
                )}
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Benutzerlimit (leer = unbegrenzt)</label>
                  <input
                    type="number" className="form-input" value={newLicenseUserLimit}
                    onChange={e => setNewLicenseUserLimit(e.target.value)}
                    min="1" disabled={loading} placeholder="Unbegrenzt"
                  />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Laufzeit</label>
                  <select
                    className="form-select" value={newLicenseDurationType}
                    onChange={e => setNewLicenseDurationType(e.target.value)} disabled={loading}
                  >
                    <option value="WEEK">Woche</option>
                    <option value="MONTH">Monat</option>
                    <option value="YEAR">Jahr</option>
                    <option value="UNLIMITED">Unbegrenzt</option>
                  </select>
                </div>
              </div>
              <div className="form-actions">
                <button type="submit" disabled={loading} className="btn btn-primary">
                  {loading ? 'Speichern…' : (editingLicense ? 'Aktualisieren' : 'Lizenz erstellen')}
                </button>
                {editingLicense && (
                  <button type="button" onClick={cancelEditing} className="btn btn-secondary">
                    Abbrechen
                  </button>
                )}
              </div>
            </form>
          </div>

          <div className="section-header">
            <h3>Lizenzen{selectedProgram ? ` – ${selectedProgram.name}` : ''}</h3>
            {loading && <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Lädt…</span>}
          </div>

          {licenses.length === 0 && !loading ? (
            <div className="empty-state">Keine Lizenzen für dieses Programm.</div>
          ) : (
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Lizenzschlüssel</th>
                    <th>Nutzer</th>
                    <th>Produkt</th>
                    <th>Laufzeit</th>
                    <th>Läuft ab</th>
                    <th>Status</th>
                    <th>Registrierung</th>
                    <th>Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {licenses.map((license) => (
                    <React.Fragment key={license.id}>
                    <tr className={!license.is_active ? 'inactive' : ''}>
                      <td style={{ color: 'var(--text-muted)', width: '40px' }}>{license.id}</td>
                      <td><span className="mono">{license.license_key}</span></td>
                      <td style={{ minWidth: '160px' }}>
                        {license.email ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                            <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{license.email}</span>
                            {license.discord_username ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                {license.discord_avatar_url && (
                                  <img src={license.discord_avatar_url} alt="" style={{ width: 16, height: 16, borderRadius: '50%' }} />
                                )}
                                <span style={{ fontSize: '11px', color: '#a5b4fc' }}>
                                  {license.discord_display_name || license.discord_username}
                                </span>
                              </div>
                            ) : null}
                          </div>
                        ) : (
                          <span style={{ color: 'var(--text-dim)', fontSize: '12px' }}>–</span>
                        )}
                      </td>
                      <td>
                        {license.product
                          ? <span className="badge badge-cyan">{license.product.name}</span>
                          : <span style={{ color: 'var(--text-dim)', fontSize: '12px' }}>–</span>
                        }
                      </td>
                      <td>
                        <span className="badge badge-cyan">
                          {DURATION_LABELS[license.duration_type] || license.duration_type}
                        </span>
                      </td>
                      <td style={{ color: 'var(--text-muted)', fontSize: '12px' }}>
                        {formatDate(license.expires_at)}
                      </td>
                      <td>
                        <button
                          onClick={() => handleToggleActive(license)}
                          className={`badge ${license.is_active ? 'badge-active' : 'badge-inactive'}`}
                          style={{ background: 'none', border: '1px solid', cursor: 'pointer', font: 'inherit' }}
                        >
                          {license.is_active ? 'Aktiv' : 'Inaktiv'}
                        </button>
                      </td>
                      <td>
                        {license.activation ? (
                          <div className="activation-info">
                            <div className="badge badge-active" style={{ marginBottom: '4px' }}>Registriert</div>
                            <div className="activation-hw mono" title={license.activation.hardware_id}>
                              {license.activation.hardware_id.substring(0, 14)}…
                            </div>
                            <div className="activation-meta">IP: {license.activation.ip_address}</div>
                            <div className="activation-meta" style={{ marginBottom: '6px' }}>
                              Zuletzt: {formatDate(license.activation.last_seen_at)}
                            </div>
                            <button
                              onClick={() => handleRemoveActivation(license.id)}
                              className="btn btn-secondary btn-xs"
                            >
                              Entregistrieren
                            </button>
                          </div>
                        ) : (
                          <span style={{ color: 'var(--text-dim)', fontSize: '12px' }}>Nicht registriert</span>
                        )}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button onClick={() => toggleDetails(license.id)} className="btn btn-secondary btn-xs">
                            {expandedLicenseId === license.id ? 'Details ausblenden' : 'Details'}
                          </button>
                          <button onClick={() => startEditing(license)} className="btn btn-secondary btn-xs">
                            Bearbeiten
                          </button>
                          <button onClick={() => handleDeleteLicense(license.id)} className="btn btn-danger btn-sm">
                            Löschen
                          </button>
                        </div>
                      </td>
                    </tr>
                    {expandedLicenseId === license.id && (
                      <tr className="license-details-row">
                        <td colSpan={9}>
                          <div className="license-details">
                            <div className="license-details-grid">
                              <div className="license-details-item">
                                <span className="license-details-label">Lizenzschlüssel</span>
                                <span className="mono">{license.license_key}</span>
                              </div>
                              <div className="license-details-item">
                                <span className="license-details-label">Programm</span>
                                <span>{selectedProgram?.name || '–'}</span>
                              </div>
                              <div className="license-details-item">
                                <span className="license-details-label">E-Mail</span>
                                <span>{license.email || '–'}</span>
                              </div>
                              {license.discord_username && (
                                <div className="license-details-item">
                                  <span className="license-details-label">Discord</span>
                                  <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    {license.discord_avatar_url && (
                                      <img src={license.discord_avatar_url} alt="" style={{ width: 20, height: 20, borderRadius: '50%' }} />
                                    )}
                                    {license.discord_display_name || license.discord_username}
                                    <span style={{ color: 'var(--text-dim)', fontSize: '11px' }}>(@{license.discord_username})</span>
                                  </span>
                                </div>
                              )}
                              <div className="license-details-item">
                                <span className="license-details-label">Benutzerlimit</span>
                                <span>{license.user_limit ?? 'Unbegrenzt'}</span>
                              </div>
                              <div className="license-details-item">
                                <span className="license-details-label">Kundenlimit</span>
                                <span>{license.customer_limit ?? 'Unbegrenzt'}</span>
                              </div>
                              <div className="license-details-item">
                                <span className="license-details-label">Plan / Module</span>
                                <span>
                                  {license.entitlement_version > 0
                                    ? `${license.plan_key || '–'} · ${license.modules.join(', ') || 'keine'}`
                                    : 'live vom Produkt (Altbestand)'}
                                </span>
                              </div>
                              <div className="license-details-item">
                                <span className="license-details-label">Entitlement-Version</span>
                                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  {license.entitlement_version}
                                  {license.product && (
                                    <button type="button" className="btn btn-secondary btn-xs"
                                      onClick={() => handleEntitlementFromProduct(license)}>
                                      vom Produkt übernehmen
                                    </button>
                                  )}
                                </span>
                              </div>
                              {license.parent_license_id && (
                                <div className="license-details-item">
                                  <span className="license-details-label">Add-on zu</span>
                                  <span>Lizenz #{license.parent_license_id}</span>
                                </div>
                              )}
                              {license.trial_pending && (
                                <div className="license-details-item">
                                  <span className="license-details-label">Test</span>
                                  <span>vergeben, startet mit der ersten Aktivierung</span>
                                </div>
                              )}
                              <div className="license-details-item">
                                <span className="license-details-label">Erstellt am</span>
                                <span>{formatDate(license.created_at)}</span>
                              </div>
                              <div className="license-details-item">
                                <span className="license-details-label">Aktualisiert am</span>
                                <span>{formatDate(license.updated_at)}</span>
                              </div>
                              <div className="license-details-item">
                                <span className="license-details-label">Läuft ab</span>
                                <span>{formatDate(license.expires_at)}</span>
                              </div>
                            </div>

                            {license.product && (
                              <>
                                <div className="license-details-subtitle">Produkt</div>
                                <div className="license-details-grid">
                                  <div className="license-details-item">
                                    <span className="license-details-label">Name</span>
                                    <span>{license.product.name}</span>
                                  </div>
                                  <div className="license-details-item">
                                    <span className="license-details-label">Preis</span>
                                    <span>{parseFloat(license.product.price).toFixed(2)} {license.product.currency}</span>
                                  </div>
                                  <div className="license-details-item">
                                    <span className="license-details-label">Plan-Key</span>
                                    <span className="mono">{license.product.plan_key || '–'}</span>
                                  </div>
                                  <div className="license-details-item" style={{ gridColumn: '1 / -1' }}>
                                    <span className="license-details-label">Module</span>
                                    <span>
                                      {license.product.modules
                                        ? license.product.modules.split(/[\n,]/).map(m => m.trim()).filter(Boolean).map(m => (
                                            <span key={m} className="badge badge-cyan" style={{ marginRight: '4px' }}>{m}</span>
                                          ))
                                        : '–'}
                                    </span>
                                  </div>
                                  {license.product.description && (
                                    <div className="license-details-item" style={{ gridColumn: '1 / -1' }}>
                                      <span className="license-details-label">Beschreibung</span>
                                      <span>{license.product.description}</span>
                                    </div>
                                  )}
                                </div>
                              </>
                            )}

                            <div className="license-details-subtitle">Aktivierung</div>
                            {license.activation ? (
                              <div className="license-details-grid">
                                <div className="license-details-item">
                                  <span className="license-details-label">Hardware-ID</span>
                                  <span className="mono">{license.activation.hardware_id}</span>
                                </div>
                                <div className="license-details-item">
                                  <span className="license-details-label">IP (Aktivierung)</span>
                                  <span className="mono">{license.activation.ip_address}</span>
                                </div>
                                <div className="license-details-item">
                                  <span className="license-details-label">IP (zuletzt gesehen)</span>
                                  <span className="mono">{license.activation.last_seen_ip}</span>
                                </div>
                                <div className="license-details-item">
                                  <span className="license-details-label">Aktiviert am</span>
                                  <span>{formatDate(license.activation.activated_at)}</span>
                                </div>
                                <div className="license-details-item">
                                  <span className="license-details-label">Zuletzt gesehen</span>
                                  <span>{formatDate(license.activation.last_seen_at)}</span>
                                </div>
                              </div>
                            ) : (
                              <span style={{ color: 'var(--text-dim)', fontSize: '12px' }}>Noch nicht registriert.</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {!selectedProgramId && (
        <div className="empty-state">Bitte oben ein Programm auswählen.</div>
      )}
    </div>
  );
}

function StatCard({ label, value, color, small }) {
  return (
    <div style={{
      background: 'var(--surface)', border: '1px solid var(--border)',
      borderRadius: '12px', padding: '16px 18px',
      borderTop: `2px solid ${color}`,
    }}>
      <div style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.8px', textTransform: 'uppercase', color: 'var(--text-dim)', marginBottom: '6px' }}>
        {label}
      </div>
      <div style={{ fontSize: small ? '13px' : '26px', fontWeight: small ? 600 : 800, color: color, lineHeight: 1.2 }}>
        {value}
      </div>
    </div>
  );
}

export default LicenseManagement;
